import { useNavigationBlocker } from '@/hooks/useNavigationBlocker';
import { Form, Quiz } from '@superfunnel/types';
import { QueryDocumentSnapshot } from 'firebase/firestore';
import { useCallback } from 'react';
import { WebsiteUpdate } from '../../../contexts/WebsiteUpdaterContext';
import { updateVariant } from '../../../services/DynamicPageVariantsService';
import {
  updateDynamicPage,
  updateWebsiteStatus,
} from '../../../services/DynamicPagesService';

export interface UseEditorActionsReturn {
  onOpenSidebar: () => void;
  onCloseSidebar: () => void;
  onCloseEditor: () => void;
  handleWebsiteUpdateEvent: (wu: WebsiteUpdate) => void;
  toggleVisualEditMode: () => void;
  moveFromDraft: () => void;
}

interface UseEditorActionsProps {
  isEditable: boolean;
  previewMode: boolean;
  agentHasUnreviewedChanges: boolean;
  setSidebarCollapsed: (collapsed: boolean) => void;
  onClose: () => void;
  // Modal setters
  setActiveWebsiteUpdate: (wu: WebsiteUpdate | undefined) => void;
  setAddSectionModalOpen: (open: boolean) => void;
  setSelectImageModalOpen: (open: boolean) => void;
  setDeleteSectionModalOpen: (open: boolean) => void;
  setAddSectionItemModalOpen: (open: boolean) => void;
  setSectionItemEditModalOpen: (sectionType: any) => void;
  setSidebarOpen: (open: boolean) => void;
  setEditFormModalOpen: (state: any) => void;
  setEditHtmlStepperModalOpen: (state: any) => void;
  setReplaceFormModalOpen: (state: any) => void;
  setConvertGlobalToLocalModalOpen: (open: boolean) => void;
  setConvertLocalToGlobalModalOpen: (state: any) => void;
  setEditQuillSearchParamMentionModalOpen: (open: boolean) => void;
  setEditCTAButtonModalOpen: (open: boolean) => void;
  updateWebsiteByEvent: (wu: WebsiteUpdate) => void;
  website?: any;
  forms?: QueryDocumentSnapshot<Form>[];
  quizzes?: QueryDocumentSnapshot<Quiz>[];
  // Visual edit mode
  isVisualEditMode: boolean;
  setIsVisualEditMode: (mode: boolean) => void;
  sendMessageToIframe?: (message: any) => void;
  // Move from draft props
  accountId?: string;
  dynamicPageId?: string;
  variantId?: string;
  websiteId?: string;
  dynamicPage?: any;
  variant?: any;
}

export function useEditorActions({
  isEditable,
  previewMode,
  agentHasUnreviewedChanges,
  setSidebarCollapsed,
  onClose,
  setActiveWebsiteUpdate,
  setAddSectionModalOpen,
  setSelectImageModalOpen,
  setDeleteSectionModalOpen,
  setAddSectionItemModalOpen,
  setSectionItemEditModalOpen,
  setSidebarOpen,
  setEditFormModalOpen,
  setEditHtmlStepperModalOpen,
  setReplaceFormModalOpen,
  setConvertGlobalToLocalModalOpen,
  setConvertLocalToGlobalModalOpen,
  setEditQuillSearchParamMentionModalOpen,
  setEditCTAButtonModalOpen,
  updateWebsiteByEvent,
  website,
  forms,
  quizzes,
  isVisualEditMode,
  setIsVisualEditMode,
  sendMessageToIframe,
  accountId,
  dynamicPageId,
  variantId,
  websiteId,
  dynamicPage,
  variant,
}: UseEditorActionsProps): UseEditorActionsReturn {
  const onOpenSidebar = useCallback(() => {
    setSidebarCollapsed(true);
  }, [setSidebarCollapsed]);

  const onCloseSidebar = useCallback(() => {
    setSidebarCollapsed(false);
  }, [setSidebarCollapsed]);

  const onCloseEditor = useCallback(() => {
    onClose();
  }, [onClose]);

  const toggleVisualEditMode = useCallback(() => {
    if (!sendMessageToIframe) {
      console.error('sendMessageToIframe is not available');
      return;
    }

    const newMode = !isVisualEditMode;
    setIsVisualEditMode(newMode);

    if (newMode) {
      sendMessageToIframe({
        eventName: 'previewConfigUpdated',
        isEditable: false,
      });
      sendMessageToIframe({ eventName: 'enterSelectionMode' });
    } else {
      if (isEditable) {
        sendMessageToIframe({
          eventName: 'previewConfigUpdated',
          isEditable: previewMode ? false : true,
        });
      }
      sendMessageToIframe({ eventName: 'exitSelectionMode' });
    }
  }, [
    sendMessageToIframe,
    isVisualEditMode,
    setIsVisualEditMode,
    isEditable,
    previewMode,
  ]);

  const handleWebsiteUpdateEvent = useCallback(
    (wu: WebsiteUpdate) => {
      if (wu?.type === 'addSectionBelow') {
        setActiveWebsiteUpdate(wu);
        setAddSectionModalOpen(true);
        return;
      }
      if (wu?.type === 'selectImage' || wu?.type === 'selectThumbnail') {
        setActiveWebsiteUpdate(wu);
        setSelectImageModalOpen(true);
        return;
      }
      if (wu?.type === 'deleteSection') {
        setActiveWebsiteUpdate(wu);
        setDeleteSectionModalOpen(true);
        return;
      }
      if (wu?.type === 'addSectionItem') {
        const sectionType = website?.development.sections.find(
          (i: any) => i.id === wu?.sectionId,
        )?.type;
        if (
          sectionType === 'comparisonList' ||
          sectionType === 'comparisonTopItems'
        ) {
          setActiveWebsiteUpdate(wu);
          setAddSectionItemModalOpen(true);
          return;
        }
      }
      if (wu?.type === 'editSectionItem') {
        const sectionType = website?.development.sections.find(
          (i: any) => i.id === wu?.sectionId,
        )?.type;
        setActiveWebsiteUpdate(wu);
        setSectionItemEditModalOpen(sectionType);
        return;
      }
      if (wu?.type === 'editSection') {
        setActiveWebsiteUpdate(wu);
        setSidebarOpen(true);
        return;
      }
      if (wu?.type === 'editForm') {
        const form = forms?.find((f) => f.id === wu.content.formId);
        const quiz = quizzes?.find((q) =>
          q.data().formIds.includes(wu.content.formId),
        );

        if (!form && !quiz) {
          console.warn(
            '[WEBSITE_UPDATER] editForm event received but form not found',
            wu,
          );
          return;
        }
        setActiveWebsiteUpdate(wu);

        if (quiz) {
          // Opening a quiz form - use separate modal state
          setEditHtmlStepperModalOpen({
            open: true,
            quizId: quiz.id,
            formId: wu.content.formId,
          });
        } else {
          setEditFormModalOpen({
            open: true,
            form: form!.data(),
            formId: wu.content.formId,
          });
        }
        return;
      }
      if (wu?.type === 'replaceForm') {
        setActiveWebsiteUpdate(wu);
        setReplaceFormModalOpen({
          open: true,
          sectionId: wu.sectionId,
          currentFormId: wu.content.formId,
        });
        return;
      }
      if (wu?.type === 'editButton') {
        setActiveWebsiteUpdate(wu);
        setEditCTAButtonModalOpen(true);
        return;
      }
      if (wu?.type === 'convertGlobalToLocal') {
        setActiveWebsiteUpdate(wu);
        setConvertGlobalToLocalModalOpen(true);
        return;
      }
      if (wu?.type === 'convertLocalToGlobal') {
        setActiveWebsiteUpdate(wu);
        setConvertLocalToGlobalModalOpen({});
        return;
      }
      if (wu?.type === 'customValueMentionClick') {
        setActiveWebsiteUpdate(wu);
        setEditQuillSearchParamMentionModalOpen(true);
        return;
      }

      updateWebsiteByEvent(wu);
    },
    [
      updateWebsiteByEvent,
      setActiveWebsiteUpdate,
      setAddSectionModalOpen,
      setSelectImageModalOpen,
      setDeleteSectionModalOpen,
      website?.development.sections,
      setAddSectionItemModalOpen,
      setSectionItemEditModalOpen,
      setSidebarOpen,
      forms,
      quizzes,
      setEditFormModalOpen,
      setEditHtmlStepperModalOpen,
      setReplaceFormModalOpen,
      setEditCTAButtonModalOpen,
      setConvertGlobalToLocalModalOpen,
      setConvertLocalToGlobalModalOpen,
      setEditQuillSearchParamMentionModalOpen,
    ],
  );

  // Browser/tab close listener - only block if there are unreviewed agent changes
  useNavigationBlocker(
    agentHasUnreviewedChanges,
    'You have an active conversation. Are you sure you want to leave? Your progress will be lost.',
  );

  const moveFromDraft = useCallback(async () => {
    // Check if we have the necessary IDs
    if (!accountId || !dynamicPageId) {
      console.warn('[MOVE_FROM_DRAFT] Missing required IDs');
      return;
    }

    try {
      // Check website status first
      if (websiteId && website?.status === 'draft') {
        await updateWebsiteStatus(
          accountId,
          dynamicPageId,
          websiteId,
          'active',
        );
        console.log('[MOVE_FROM_DRAFT] Updated website to active');
      }

      // Check variant status
      if (variantId && variant?.status === 'draft') {
        await updateVariant(accountId, dynamicPageId, variantId, {
          status: 'active',
        });
        console.log('[MOVE_FROM_DRAFT] Updated variant to active');
      }

      // Check dynamic page status
      if (dynamicPage?.status === 'draft') {
        await updateDynamicPage(accountId, dynamicPageId, {
          status: 'disabled',
        });
        console.log('[MOVE_FROM_DRAFT] Updated dynamic page to disabled');
      }

      console.log('[MOVE_FROM_DRAFT] No draft items found to update');
    } catch (error) {
      console.error('[MOVE_FROM_DRAFT] Error updating status:', error);
    }
  }, [
    accountId,
    dynamicPageId,
    websiteId,
    variantId,
    website,
    variant,
    dynamicPage,
  ]);

  return {
    onOpenSidebar,
    onCloseSidebar,
    onCloseEditor,
    handleWebsiteUpdateEvent,
    toggleVisualEditMode,
    moveFromDraft,
  };
}
